import React, {useState, useRef} from 'react';
import {UIMessage} from '@ai-sdk/react';
import {
  Box,
  VStack,
  Flex,
  Text,
  Input,
  Spinner,
  HStack,
  Button,
} from '@chakra-ui/react';

export const CampaignsChatThread = ({
  isLoading,
  messages,
  onNewMessage,
  onUpdateCampaigns,
}: {
  isLoading?: boolean;
  messages: UIMessage[];
  onNewMessage: (message: string) => void;
  onUpdateCampaigns?: () => void;
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const [input, setInput] = useState('');

  return (
    <Flex direction="column" h="100%" w="100%">
      <Box ref={messagesContainerRef} flex="1" overflowY="auto" p={4}>
        <VStack spacing={4} align="stretch">
          {messages.length === 0 ? (
            <Text color="gray.500" textAlign="center" mt={8}>
              Start a conversation by typing a message below.
            </Text>
          ) : (
            messages.map((message, index) => (
              <Box
                key={message.id}
                alignSelf={message.role === 'user' ? 'flex-end' : 'flex-start'}
                maxW="70%"
              >
                <Box
                  bg={message.role === 'user' ? 'blue.500' : 'gray.100'}
                  color={message.role === 'user' ? 'white' : 'black'}
                  p={3}
                  borderRadius="lg"
                  boxShadow="sm"
                >
                  {message.parts.map((part, index) => (
                    <Text key={index} whiteSpace="pre-wrap">
                      {part.type === 'text' ? part.text : null}
                    </Text>
                  ))}
                </Box>
                {/* Show Update Campaigns button after assistant messages when not loading */}
                {/* TODO: Investigate if this can be handled via tool calls */}
                {message.role === 'assistant' &&
                  messages.length > 1 &&
                  !isLoading &&
                  index === messages.length - 1 && (
                    <Box mt={2} alignSelf="flex-start">
                      <Button
                        size="sm"
                        colorScheme="green"
                        onClick={onUpdateCampaigns}
                        variant="outline"
                      >
                        Update Campaigns
                      </Button>
                    </Box>
                  )}
              </Box>
            ))
          )}
          {isLoading && (
            <Box alignSelf="flex-start" maxW="70%">
              <Box bg="gray.100" p={3} borderRadius="lg" boxShadow="sm">
                <HStack spacing={2}>
                  <Spinner size="sm" />
                  <Text>Keyplay AI is thinking...</Text>
                </HStack>
              </Box>
            </Box>
          )}
          {/* Invisible div to scroll to */}
          <div ref={messagesEndRef} />
        </VStack>
      </Box>

      <Box p={4} borderTop="1px" borderColor="gray.200" bg="white">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setInput('');
            onNewMessage(input);
          }}
        >
          <HStack spacing={3}>
            <Input
              borderRadius="lg"
              isDisabled={isLoading}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask me to refine your campaigns..."
              value={input}
            />
            <Button
              borderRadius="lg"
              colorScheme="blue"
              isDisabled={!input.trim()}
              type="submit"
            >
              Send
            </Button>
          </HStack>
        </form>
      </Box>
    </Flex>
  );
};
